import { useState, useEffect } from 'react';
import { Zap, Settings, CheckCircle, XCircle, Clock, AlertTriangle, Save, RotateCcw, Search, RefreshCw } from 'lucide-react';
import { tenantConfigurationService } from '../../../services/tenantConfigurationService';

export default function FeatureToggleMatrix({ organization, onFeatureToggled }) {
  const [featureFlags, setFeatureFlags] = useState([]);
  const [orgFeatureFlags, setOrgFeatureFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [changedFlags, setChangedFlags] = useState(new Map());

  useEffect(() => {
    loadFeatureData();
  }, [organization?.id]);

  const loadFeatureData = async () => {
    try {
      setLoading(true);
      const [allFlags, orgFlags] = await Promise.all([
        tenantConfigurationService?.getFeatureFlags(),
        organization?.id ? tenantConfigurationService?.getOrganizationFeatureFlags(organization?.id) : Promise.resolve([])
      ]);

      setFeatureFlags(allFlags);
      setOrgFeatureFlags(orgFlags);
      setChangedFlags(new Map());
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getFeatureValue = (flagId) => {
    const orgFlag = orgFeatureFlags?.find(f => f?.featureFlagId === flagId);
    if (orgFlag) {
      return orgFlag?.value;
    }
    
    const defaultFlag = featureFlags?.find(f => f?.id === flagId);
    return defaultFlag?.defaultValue || 'false';
  };

  const getFeatureStatus = (flagId) => {
    const value = getFeatureValue(flagId);
    const flag = featureFlags?.find(f => f?.id === flagId);
    
    if (flag?.flagType === 'boolean') {
      return value === 'true' ? 'enabled' : 'disabled';
    }
    
    return 'configured';
  };

  const handleFeatureToggle = async (flagId, newValue) => {
    const currentValue = getFeatureValue(flagId);
    
    if (currentValue === newValue?.toString()) {
      return; // No change
    }

    setChangedFlags(prev => new Map(prev)?.set(flagId, newValue?.toString()));
  };

  const saveChanges = async () => {
    if (changedFlags?.size === 0) return;
    
    try {
      setSaving(true);
      setError('');
      
      const savePromises = Array.from(changedFlags?.entries())?.map(([flagId, value]) =>
        tenantConfigurationService?.updateOrganizationFeatureFlag(organization?.id, flagId, value)
      );
      
      await Promise.all(savePromises);
      
      // Reload data to get updated state
      await loadFeatureData();
      onFeatureToggled();
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const resetChanges = () => {
    setChangedFlags(new Map());
  };

  const getDisplayValue = (flagId) => {
    if (changedFlags?.has(flagId)) {
      return changedFlags?.get(flagId);
    }
    return getFeatureValue(flagId);
  };

  const getCategories = () => {
    const categories = new Set();
    featureFlags?.forEach(flag => {
      const category = flag?.name?.split('_')?.[0] || 'general';
      categories?.add(category);
    });
    return Array.from(categories);
  };

  const filteredFlags = featureFlags?.filter(flag => {
    if (searchQuery && !flag?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) && 
        !flag?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())) {
      return false;
    }
    
    if (categoryFilter !== 'all') {
      const category = flag?.name?.split('_')?.[0] || 'general';
      if (category !== categoryFilter) return false;
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading feature flags...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Feature Toggle Matrix</h2>
          <p className="text-gray-600">
            Granular control over functionality access for {organization?.name}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadFeatureData}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          {changedFlags?.size > 0 && (
            <>
              <button
                onClick={resetChanges}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
              >
                <RotateCcw className="h-4 w-4" />
                <span>Reset</span>
              </button>
              <button
                onClick={saveChanges}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Saving...' : `Save ${changedFlags?.size} Changes`}</span>
              </button>
            </>
          )}
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}
      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search feature flags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Categories</option>
            {getCategories()?.map(category => (
              <option key={category} value={category}>
                {category?.charAt(0)?.toUpperCase() + category?.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Changes Summary */}
      {changedFlags?.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-blue-800 font-medium">
              {changedFlags?.size} unsaved change{changedFlags?.size !== 1 ? 's' : ''}
            </span>
          </div>
          <p className="text-blue-700 text-sm mt-1">
            Click "Save Changes" to apply your modifications, or "Reset" to discard them.
          </p>
        </div>
      )}
      {/* Feature Flags Matrix */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Feature Configuration</h3>
          <p className="text-sm text-gray-600">
            {filteredFlags?.length} feature{filteredFlags?.length !== 1 ? 's' : ''} available for configuration
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Feature
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Default
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFlags?.map((flag) => {
                const currentValue = getDisplayValue(flag?.id);
                const hasChanges = changedFlags?.has(flag?.id);
                const status = getFeatureStatus(flag?.id);
                
                return (
                  <tr key={flag?.id} className={hasChanges ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <Zap className={`h-4 w-4 ${
                            status === 'enabled' ? 'text-green-500' : 'text-gray-400'
                          }`} />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {flag?.name?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                          </div>
                          {flag?.description && (
                            <div className="text-sm text-gray-500 mt-1">
                              {flag?.description}
                            </div>
                          )}
                          {hasChanges && (
                            <div className="text-xs text-blue-600 mt-1 font-medium">
                              • Modified (unsaved)
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        {flag?.flagType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {status === 'enabled' ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                            <span className="text-green-700 text-sm font-medium">Enabled</span>
                          </>
                        ) : status === 'disabled' ? (
                          <>
                            <XCircle className="h-4 w-4 text-red-500 mr-1" />
                            <span className="text-red-700 text-sm font-medium">Disabled</span>
                          </>
                        ) : (
                          <>
                            <Settings className="h-4 w-4 text-blue-500 mr-1" />
                            <span className="text-blue-700 text-sm font-medium">Configured</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {flag?.flagType === 'boolean' ? (
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={currentValue === 'true'}
                            onChange={(e) => handleFeatureToggle(flag?.id, e?.target?.checked)}
                            className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                          />
                          <span className="ml-2 text-sm text-gray-900">
                            {currentValue === 'true' ? 'On' : 'Off'}
                          </span>
                        </label>
                      ) : flag?.flagType === 'string' ? (
                        <input
                          type="text"
                          value={currentValue}
                          onChange={(e) => handleFeatureToggle(flag?.id, e?.target?.value)}
                          className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter value"
                        />
                      ) : (
                        <input
                          type="number"
                          value={currentValue}
                          onChange={(e) => handleFeatureToggle(flag?.id, e?.target?.value)}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {flag?.defaultValue}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {hasChanges ? (
                          <button
                            onClick={() => setChangedFlags(prev => {
                              const newMap = new Map(prev);
                              newMap?.delete(flag?.id);
                              return newMap;
                            })}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            title="Reset this change"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                        ) : (
                          <div className="w-6 h-6"></div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredFlags?.length === 0 && (
          <div className="text-center py-12">
            <Zap className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No feature flags found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery || categoryFilter !== 'all' ?'Try adjusting your search or filters' :'No feature flags are currently configured'
              }
            </p>
          </div>
        )}
      </div>
      {/* Feature Categories Summary */}
      {filteredFlags?.length > 0 && (
        <div className="mt-6 bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Feature Categories</h4>
          <div className="flex flex-wrap gap-2">
            {getCategories()?.map(category => {
              const categoryFlags = filteredFlags?.filter(f => 
                (f?.name?.split('_')?.[0] || 'general') === category
              );
              const enabledCount = categoryFlags?.filter(f => 
                getDisplayValue(f?.id) === 'true'
              )?.length;
              
              return (
                <div
                  key={category}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
                >
                  <span className="capitalize">{category}</span>
                  <span className="ml-2 px-2 py-0.5 bg-white rounded-full text-xs">
                    {enabledCount}/{categoryFlags?.length}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}